import React, { ComponentType, ReactNode, useMemo } from 'react';

import { intersectionWith } from 'lodash';
// @ts-ignore-next-line
import store from 'store';

const ROLES = [
	'Super Admin',
	'Admin',
	'Methodist',
	'Teacher',
	'Mentor/Tutor',
	'Student',
	'CG Student',
	'CG Coordinator',
	'CG Counselor',
	'CG Parent',
	'Partner',
	'Parent',
	'Basic User',
	'Auditor',
	'Demo TIS',
	'Partner Invitee',
	'Manager',
] as const;

export const ROLE = {
	SUPER_ADMIN: 'Super Admin',
	ADMIN: 'Admin',
	METHODIST: 'Methodist',
	TEACHER: 'Teacher',
	MENTOR_TUTOR: 'Mentor/Tutor',
	STUDENT: 'Student',
	CG_STUDENT: 'CG Student',
	CG_COORDINATOR: 'CG Coordinator',
	CG_COUNSELOR: 'CG Counselor',
	CG_PARENT: 'CG Parent',
	PARENT: 'Parent',
	PARTNER: 'Partner',
	BASIC_USER: 'Basic User',
	DEMO_TIS: 'Demo TIS',
	PARTNER_INVITEE: 'Partner Invitee',
	AUDITOR: 'Auditor',
	MANAGER: 'Manager',
} as const;

export type Role = (typeof ROLES)[number];
type UserRole = any;

type RBACBaseProps = {
	granted?: Role[];
};

type RBACProps = RBACBaseProps & {
	children: ReactNode;
	fallback?: ReactNode;
	grantedOnly?: boolean;
};

const defaultGranted = ['Super Admin', 'Admin'];

export function checkAccess(granted: Role[] = [], grantedOnly = false) {
	const grantedTo = [...(grantedOnly ? [] : defaultGranted), ...granted];
	const intersection = intersectionWith(
		grantedTo,
		store.auth.user.roles,
		(grantedRole: Role, userRole: UserRole) => userRole.name === grantedRole,
	);
	return !!intersection.length;
}

export function checkRole(role: Role) {
	return !!store.auth.user.roles.find((userRole: UserRole) => userRole.name === role);
}

function RBAC({ children, fallback = null, granted = [], grantedOnly = false }: RBACProps) {
	const isGranted = useMemo(() => checkAccess(granted, grantedOnly), [granted, grantedOnly]);
	return (
		// eslint-disable-next-line react/jsx-no-useless-fragment
		<>{isGranted ? children : fallback}</>
	);
}

export default RBAC;

export function withRBAC(granted: Role[] = []) {
	return function hoc<ComponentProps>(Component: ComponentType<ComponentProps>) {
		return function ComponentWithRBAC(props: ComponentProps) {
			const isGranted = useMemo(() => checkAccess(granted), [granted]);
			if (!isGranted) {
				throw 403;
			}
			return <Component {...props} />;
		};
	};
}

export function checkImpersonateRole(role: Role): boolean {
	return store.auth.activeRole === role;
}
